import React, { useEffect, useRef, useState } from "react";
import L from "leaflet";
import 'leaflet/dist/leaflet.css';
import { clearLayerLegend, createControl, createMap, initMinimalMap, initModalLegendWithIcon, initOutlineCity, positionMarker } from "../../../../utils/map/mapUtils";
import { getNode, onlyUniqueByKey } from "../../../../utils/overlaysUtils";
import { Caption } from "./Caption";
import { SearchBar } from "./SearchBar";
require("../../../../utils/leaflet/Leaflet.VectorGrid.bundled")

export const MapContainer = ({selectedItem, addressInfos, outlineCity, scenario, updateAddress, onSelectScenario}) => {
    const [isCaptionOpen, setIsCaptionOpen] = useState(false);
    const [alternativeText, setAlternativeText] = useState(null);
    const [legendsValues, setLegendValues] = useState(null);

    const mapRef = useRef(undefined);
    const overlaysGroupRef = useRef(undefined);
    const legendRef = useRef(undefined);
    const outlineCityRef = useRef(undefined);
    const layerControlRef = useRef(undefined);

    useEffect(() => {
        if(outlineCity){
            mapRef.current = createMap('city');
            legendRef.current = createControl('topright', false, true);
            outlineCityRef.current = initOutlineCity(outlineCity.COM);
            overlaysGroupRef.current = L.layerGroup().addTo(mapRef.current);

            initMinimalMap(mapRef.current, null, outlineCityRef.current, addressInfos, 'bottomright');
            initModalLegendWithIcon(legendRef.current, addressInfos, mapRef.current, () => setIsCaptionOpen(true))
        }
    }, [])

    useEffect(() => {
        if(mapRef.current && legendRef.current){
            // Remove all data layers, including those added outside the overlay group by leaflet's layers control
            mapRef.current.eachLayer(l => {
                if (l.options.isDataLayer) {
                    l.remove();
                }
            });
            mapRef.current.closePopup();

            //keep previous zoom
            if(getNode(addressInfos, ['address', 'lat']) && getNode(addressInfos, ['address', 'lon'])){
                mapRef.current.setView([addressInfos.address.lat, addressInfos.address.lon], 14);
            } else {
                mapRef.current.fitBounds(outlineCityRef.current.getBounds());
            }

            if(selectedItem && selectedItem.maps){
                const layers = selectedItem.maps.map((map, idx) => {
                    const tilesId = map.tilesId || selectedItem.id;
                    //legend values & tile
                    return fetch(`/values/${tilesId}`)
                        .then(res => res.json())
                        .then(values => {
                            const formattedValues = selectedItem.legend ? selectedItem.legend(values) : values.filter(onlyUniqueByKey("label"));
                            return formattedValues;
                        })
                        .then(legends => {
                            let tileLayer = L.vectorGrid.protobuf(`/tiles/${tilesId}/{z}/{x}/{y}.pbf`, {
                                interactive: true,
                                vectorTileLayerStyles: {[tilesId]: (properties) => map.style(properties, legends)},
                                attribution: map.attributions ? `© ${map.attributions}` : null,
                                zIndex: 10 + idx,
                                isDataLayer: true
                            })
                                .on('click', (layerElement) => {
                                    if (map.popupContent) {
                                        L.popup()
                                            .setLatLng(layerElement.latlng)
                                            .setContent(map.popupContent(layerElement.layer.properties))
                                            .openOn(mapRef.current);
                                    }
                                });

                            return {
                                ...map,
                                legends,
                                tileLayer
                            };
                        })
                        .catch(error => {
                            console.error(`Failed to load layer ${tilesId}`);
                            return null;
                        });
                });

                Promise.all(layers)
                    .then(layerInfos => {

                        const filteredLayers = layerInfos.filter(l => l !== null);

                        const showLayersControl = filteredLayers.some(l => l.showInLayersControl);

                        // Show / hide layers control
                        if (layerControlRef.current) {
                            layerControlRef.current.remove();
                        }
                        if (showLayersControl) {
                            const tileLayers = Object.fromEntries(
                                filteredLayers
                                    .filter(l => l.showInLayersControl)
                                    .map(l => [l.layerLabel || selectedItem.label, l.tileLayer])
                            );
                            layerControlRef.current = L.control.layers(tileLayers, null, {collapsed: false, position: 'topleft', autoZIndex: false}).addTo(mapRef.current);

                            // select scenario layer from url to leaflet
                            scenario && tileLayers[`Scénario ${scenario}m³`].addTo(mapRef.current);

                            //to display scenario in url
                            mapRef.current.on('baselayerchange', (e) => {
                                if(!mapRef.current.hasLayer(tileLayers[`Scénario ${scenario}m³`])){
                                    onSelectScenario(selectedItem.id, e.name.slice(9,12))
                                }
                            })
                        }

                        // Add layer(s) to be displayed
                        filteredLayers.forEach((l, idx) => {
                            if (!l.showInLayersControl || idx === 0) {
                                overlaysGroupRef.current.addLayer(l.tileLayer);
                            }
                        });

                        // Set legend
                        setLegendValues(
                            filteredLayers
                                .filter(l => l.showLegend || !('showLegend' in l))
                                .filter(l => l.legends.length > 0)
                                .map(l => {
                                    return {
                                        label: l.legendLabel || selectedItem.label,
                                        legends: l.legends
                                    }
                                })
                        );

                        // Set alt text
                        const texts = filteredLayers
                            .map(l => l.alternativeText)
                            .filter(l => l)
                            .join('<br>');
                        setAlternativeText(texts ? texts : null);
                    });
            } else {
                clearLayerLegend(legendRef.current);
            }
        }
    }, [selectedItem])

    useEffect(() => {
        if(addressInfos && addressInfos.address && outlineCityRef.current){
            positionMarker(addressInfos.address, mapRef.current, outlineCityRef.current);
        }
    }, [addressInfos])

    return (
        <div className="map-container map-section">
            <SearchBar addressInfos={addressInfos} updateAddress={updateAddress}/>
            <div id="city-map" style={{ height: '100%'}} role="img" aria-label={alternativeText ? alternativeText : `Carte de la ville : ${addressInfos.com.libelle}`}></div>
            {isCaptionOpen &&
                <Caption 
                    legendsValues={legendsValues}
                    selectedItem={selectedItem}
                    addressInfos={addressInfos}
                    isOpen={isCaptionOpen}
                    onClose={() => setIsCaptionOpen(false)}
                />
            }
        </div>
    )
}